import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/** Copied from public/Logo (3).jpg — simple path avoids broken loads from spaces/parentheses in URL */
const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/rail-logo.jpg`;

const RAIL_ITEMS = [
  { id: 'cockpit',    label: 'Cockpit',     path: '/',                              icon: 'home' },
  { id: 'sales',      label: 'Sales',       path: '/sales/orders',                  icon: 'sales',      module: 'sales' },
  { id: 'purchasing', label: 'Purchasing',  path: '/purchasing/orders',             icon: 'cart',       module: 'purchasing' },
  { id: 'inventory',  label: 'Inventory',   path: '/inventory/items',               icon: 'box',        module: 'inventory' },
  { id: 'production', label: 'Production',  path: '/production/orders',             icon: 'factory',    module: 'production' },
  { id: 'mrp',        label: 'MRP',         path: '/mrp/wizard',                    icon: 'layers',     module: 'mrp' },
  { id: 'calendar',   label: 'Calendar',    path: '/mrp/calendar',                  icon: 'cal',        module: 'calendar' },
  { id: 'financials', label: 'Financials',  path: '/financials/aging/customer-receivables', icon: 'chart', module: 'financials' },
];

export default function IconRail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasModuleAccess, user } = useAuth();
  const isSuperAdmin = user?.role_name === 'Super Admin';

  const isActive = (item) => {
    if (item.path === '/') return location.pathname === '/';
    return location.pathname.startsWith(item.path.split('/').slice(0, 2).join('/'));
  };

  const visibleItems = RAIL_ITEMS.filter((item) => {
    if (!item.module) return true;
    return hasModuleAccess(item.module);
  });

  return (
    <aside className="w-[60px] shrink-0 bg-sap-blue flex flex-col items-center py-3 select-none z-20">
      {/* Logo */}
      <RailButton label="Agentfloww" onClick={() => navigate('/')}>
      <div className="w-9 h-9 rounded-full overflow-hidden flex items-center justify-center bg-[#e03de8]">
  <img
    src={LOGO_SRC}
    alt="Agentfloww"
    className="w-full h-full object-cover scale-110"
  />
</div>
      </RailButton>

      <div className="mb-2" />

      {/* Nav icons */}
      <nav className="flex-1 flex flex-col items-center gap-1 w-full px-1.5">
        {visibleItems.map((item) => (
          <RailButton
            key={item.id}
            label={item.label}
            active={isActive(item)}
            onClick={() => navigate(item.path)}
          >
            <RailIcon name={item.icon} />
          </RailButton>
        ))}

        {isSuperAdmin && (
          <RailButton
            label="Administration"
            active={location.pathname.startsWith('/admin')}
            onClick={() => navigate('/admin/panel')}
            className="mt-1"
          >
            <RailIcon name="gear" />
          </RailButton>
        )}
      </nav>

      {/* Bottom actions */}
      <div className="flex flex-col items-center gap-1 w-full px-1.5 pt-2 border-t border-white/20 mt-2">
        <RailButton
          label="User Guide"
          active={location.pathname === '/guide'}
          onClick={() => navigate('/guide')}
        >
          <RailIcon name="book" />
        </RailButton>
      </div>
    </aside>
  );
}

function RailButton({ label, active, onClick, children, className = '' }) {
  return (
    <div className={`relative group w-full flex justify-center ${className}`}>
      <button
        type="button"
        onClick={onClick}
        className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all text-white
          ${active ? 'bg-white/25 shadow-sm' : 'hover:bg-white/15'}`}
      >
        {children}
      </button>
      <span
        className="absolute left-full ml-2.5 top-1/2 -translate-y-1/2 px-2.5 py-1 rounded-lg
          bg-gray-900 text-white text-xs font-semibold whitespace-nowrap
          opacity-0 invisible group-hover:opacity-100 group-hover:visible
          pointer-events-none z-[100] transition-all duration-150 shadow-lg"
        role="tooltip"
      >
        {label}
      </span>
    </div>
  );
}

function RailIcon({ name }) {
  const props = {
    width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none',
    stroke: '#FFFFFF', strokeWidth: 2.25, strokeLinecap: 'round', strokeLinejoin: 'round',
  };

  switch (name) {
    case 'home':
      return <svg {...props}><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
    case 'sales':
      return <svg {...props}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;
    case 'cart':
      return <svg {...props}><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>;
    case 'box':
      return <svg {...props}><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>;
    case 'factory':
      return <svg {...props}><path d="M2 20h20M4 20V10l5-3v5l5-3v5l5-3v6"/></svg>;
    case 'layers':
      return <svg {...props}><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>;
    case 'cal':
      return <svg {...props}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
    case 'chart':
      return <svg {...props}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>;
    case 'gear':
      return <svg {...props}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>;
    case 'book':
      return <svg {...props}><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>;
    default:
      return <svg {...props}><circle cx="12" cy="12" r="10"/></svg>;
  }
}
